// The selected option as a DXF the architect can open: one layer per kind of
// thing per level, in Stereo 70 when the plot came from a cadastral plan, so
// it drops onto the survey it was drawn from.
//
// R12 (AC1009) entities only — POLYLINE/VERTEX/SEQEND, LINE, TEXT — because
// every CAD program opens R12, and it needs no handles or object tables.
import type { Generated } from "../generate.ts";
import type { MultiPoly, Pt } from "../geom.ts";
import type { Envelope } from "../envelope.ts";

/** AutoCAD colour index per layer suffix. */
const COLOUR: Record<string, number> = {
  TEREN: 7, ANVELOPA: 30, APARTAMENTE: 5, CAMERE: 8, NUCLEE: 9, CORIDOR: 2, BALCOANE: 4,
  FERESTRE: 4, USI: 1, COMERCIAL: 40, TEXT: 7, LOCURI: 8, RAMPA: 30, VERDE: 3, ALEI: 9,
  // retail parks
  UNITATI: 40, COPERTINA: 4, PAD: 6, LIVRARE: 8, DRUMURI: 9, STALPI: 7, CHEIURI: 1, VITRINE: 4, INSULE: 3, ACCES: 1,
};

export class Writer {
  private lines: string[] = [];
  readonly layers = new Set<string>();
  constructor(private origin: Pt) {}
  private pair(code: number, value: string | number) { this.lines.push(String(code), typeof value === "number" ? value.toFixed(4) : value); }
  private xy(p: Pt, cx = 10) { this.pair(cx, p[0] + this.origin[0]); this.pair(cx + 10, p[1] + this.origin[1]); this.pair(cx + 20, 0); }
  ring(layer: string, ring: Pt[]) {
    this.layers.add(layer);
    this.lines.push("0", "POLYLINE", "8", layer, "66", "1");
    this.pair(70, "1");
    for (const p of ring) { this.lines.push("0", "VERTEX", "8", layer); this.xy(p); }
    this.lines.push("0", "SEQEND", "8", layer);
  }
  poly(layer: string, mp: MultiPoly) { for (const poly of mp) for (const ring of poly) this.ring(layer, ring); }
  line(layer: string, a: Pt, b: Pt) {
    this.layers.add(layer);
    this.lines.push("0", "LINE", "8", layer);
    this.xy(a, 10); this.xy(b, 11);
  }
  text(layer: string, at: Pt, s: string, h = 0.4) {
    this.layers.add(layer);
    this.lines.push("0", "TEXT", "8", layer);
    this.xy(at); this.pair(40, h); this.lines.push("1", s.replace(/²/g, "2"));
  }
  toString() {
    const layer = (name: string) => {
      const suffix = name.split("_").pop()!;
      return ["0", "LAYER", "2", name, "70", "0", "62", String(COLOUR[suffix] ?? 7), "6", "CONTINUOUS"];
    };
    return [
      "0", "SECTION", "2", "HEADER", "9", "$ACADVER", "1", "AC1009", "9", "$INSUNITS", "70", "6", "0", "ENDSEC",
      "0", "SECTION", "2", "TABLES", "0", "TABLE", "2", "LAYER", "70", String(this.layers.size),
      ...[...this.layers].flatMap(layer), "0", "ENDTAB", "0", "ENDSEC",
      "0", "SECTION", "2", "ENTITIES", ...this.lines, "0", "ENDSEC", "0", "EOF",
    ].join("\n") + "\n";
  }
}

const centroid = (mp: MultiPoly): Pt => {
  const pts = mp.flatMap((p) => p[0]);
  return [pts.reduce((t, p) => t + p[0], 0) / pts.length, pts.reduce((t, p) => t + p[1], 0) / pts.length];
};
const levelName = (i: number) => (i < 0 ? `S${-i}` : i === 0 ? "P" : `E${i}`);
const TYPE = (t?: string) => (t === "1" ? "G" : `${t}C`);

/** The DXF text for one option. `origin` is added back to every coordinate (Stereo 70 east, north). */
export function optionDxf(g: Generated, env: Envelope, origin: Pt = [0, 0]): string {
  const w = new Writer(origin);
  w.poly("TEREN", env.plot);
  w.poly("ANVELOPA", env.storeys[0]?.footprint ?? []);
  w.poly("SITE_VERDE", g.site.green);
  w.poly("SITE_ALEI", g.site.paths);
  w.poly("SITE_RAMPA", g.site.rampOpen);

  for (const f of g.layout.floors) {
    const L = `NIV_${levelName(f.index)}`;
    for (const u of f.units) {
      const at = centroid(u.polygon);
      if (u.kind === "apartment") {
        w.poly(`${L}_APARTAMENTE`, u.polygon);
        for (const r of u.rooms ?? []) w.poly(`${L}_CAMERE`, r.polygon);
        if (u.balcony) w.poly(`${L}_BALCOANE`, u.balcony);
        w.text(`${L}_TEXT`, at, `${u.id} ${TYPE(u.type)} ${(u.usable ?? 0).toFixed(1)} m2 util`);
      } else if (u.kind === "core") {
        w.poly(`${L}_NUCLEE`, u.polygon);
        for (const p of u.parts ?? []) if (p.kind !== "shaft") w.poly(`${L}_NUCLEE`, p.polygon);
      } else if (u.kind === "corridor") w.poly(`${L}_CORIDOR`, u.polygon);
      else if (u.kind === "retail") { w.poly(`${L}_COMERCIAL`, u.polygon); w.text(`${L}_TEXT`, at, `COM ${u.area.toFixed(1)} m2`); }
      for (const o of u.openings ?? []) w.line(`${L}_${o.kind === "window" || o.kind === "shopfront" || o.kind === "balconyDoor" ? "FERESTRE" : "USI"}`, o.a, o.b);
    }
  }
  for (const lv of g.parking.plan.levels.slice(0, 1)) {
    const L = `NIV_${levelName(lv.index)}`;
    w.poly(`${L}_TEREN`, lv.footprint);
    for (const st of lv.stalls) w.poly(`${L}_LOCURI`, st);
    w.poly(`${L}_RAMPA`, lv.ramp);
  }
  return w.toString();
}
